import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCommandSource } from '../../components/CommandPalette';
import projectService from '../../utils/apiRequests/projectService';
import axiosWrapper from '../../utils/apiRequests/axiosWrapper';
import ProjectCard from './components/ProjectCard';
import ProjectListRow from './components/ProjectListRow';
import DashboardToolbar from './components/DashboardToolbar';
import CreateProjectModal from './components/CreateProjectModal';
import urls from '../../urls';
import {
  setProjects,
  setLoading as setProjectsLoading,
  setError,
  addProject,
} from '../../features/projects/projectsSlice';
import useThemeStyles from '../../hooks/useThemeStyles';
import calculateWeeklyWordCount from '../../utils/dashboard/calculateWeeklyWordCount';
import getWeeklyWordCountMessage from '../../utils/dashboard/getWeeklyWordCountMessage';
import {
  DEFAULT_PROJECT_FILTERS,
  DEFAULT_PROJECT_SORT,
  PROJECT_SORT_OPTIONS,
  filterProjects,
  sortProjects,
  hasActiveFilters,
  getPresentTypeOptions,
  getPresentStatusOptions,
} from '../../utils/dashboard/filterProjects';

const VIEW_MODE_STORAGE_KEY = 'plotwyze:dashboard:view';
const SORT_STORAGE_KEY = 'plotwyze:dashboard:sort';

const readStoredViewMode = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
};

const readStoredSort = () => {
  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    return PROJECT_SORT_OPTIONS.some((option) => option.value === stored)
      ? stored
      : DEFAULT_PROJECT_SORT;
  } catch {
    return DEFAULT_PROJECT_SORT;
  }
};

// Shared enter/exit animation for project items; `layout` makes the
// remaining items glide into their new positions instead of jumping.
const ITEM_VARIANTS = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
};
const ITEM_TRANSITION = { duration: 0.25, ease: [0.4, 0, 0.2, 1] };

const Dashboard = () => {
  const user = useSelector((state) => state.user.userInfo);
  const projects = useSelector((state) => state.projects.projects);
  const recentProjectIds = useSelector((state) => state.projects.recentProjectIds);
  const projectsLoading = useSelector((state) => state.projects.loading);
  const { colors, isDark } = useThemeStyles();
  const [loading, setLoading] = useState(true);
  const [weeklyWordCount, setWeeklyWordCount] = useState(0);
  const [isCreateProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [filters, setFilters] = useState({ ...DEFAULT_PROJECT_FILTERS });
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const [sortKey, setSortKey] = useState(readStoredSort);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Remember the preferred view and sort across visits.
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
      window.localStorage.setItem(SORT_STORAGE_KEY, sortKey);
    } catch {
      // Storage unavailable — the controls still work for this session.
    }
  }, [viewMode, sortKey]);

  // Command palette: dashboard-only actions (projects are a global source)
  useCommandSource('dashboard', () => [
    {
      id: 'dashboard:new-project',
      group: 'Actions',
      title: 'Create New Project',
      keywords: 'new project novel game story start',
      icon: <CreateNewFolderIcon />,
      perform: () => setCreateProjectModalOpen(true),
    },
    {
      id: 'dashboard:view-grid',
      group: 'Actions',
      title: 'View projects as grid',
      keywords: 'cards grid view layout',
      icon: <GridViewIcon />,
      perform: () => setViewMode('grid'),
    },
    {
      id: 'dashboard:view-list',
      group: 'Actions',
      title: 'View projects as list',
      keywords: 'list rows view layout compact',
      icon: <ViewListIcon />,
      perform: () => setViewMode('list'),
    },
  ]);

  // Fetch user settings to get weekly word count
  const userId = user?._id;
  useEffect(() => {
    const fetchWeeklyWordCount = async () => {
      try {
        const { data } = await axiosWrapper({
          method: 'get',
          path: 'users/settings',
        });
        if (data?.dailyActivity) {
          const weeklyCount = calculateWeeklyWordCount(data.dailyActivity);
          setWeeklyWordCount(weeklyCount);
        }
      } catch (err) {
        console.error('Error fetching weekly word count:', err);
      }
    };

    if (userId) {
      fetchWeeklyWordCount();
    }
  }, [userId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch projects
        dispatch(setProjectsLoading(true));
        const projectsData = await projectService.getUserProjects();
        if (projectsData?.data?.projects) {
          dispatch(
            setProjects({
              projects: projectsData.data.projects,
              recentProjectIds: projectsData.data.recentProjectIds || [],
            })
          );
        } else {
          dispatch(setError('Failed to fetch projects'));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch(setError('Failed to fetch data'));
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId, dispatch]);

  // Get recent projects (up to 3 for featured display)
  const recentProjects = recentProjectIds
    .map((id) => {
      const normalizedId = id?.toString();
      return projects.find((p) => p._id?.toString() === normalizedId || p._id === normalizedId);
    })
    .filter(Boolean)
    .slice(0, 3);

  // All projects (excluding recent ones)
  const allProjects = useMemo(() => {
    const recentIdsSet = new Set(recentProjectIds.map((id) => id?.toString()));
    return projects.filter((p) => !recentIdsSet.has(p._id?.toString()));
  }, [projects, recentProjectIds]);

  const filtersActive = hasActiveFilters(filters);

  // While filtering, search the FULL project list (recents included) so a
  // matching project is never hidden just because it sits in the Recent strip.
  const visibleProjects = useMemo(
    () => sortProjects(filtersActive ? filterProjects(projects, filters) : allProjects, sortKey),
    [filtersActive, projects, filters, allProjects, sortKey]
  );

  const typeOptions = useMemo(() => getPresentTypeOptions(projects), [projects]);
  const statusOptions = useMemo(() => getPresentStatusOptions(projects), [projects]);

  const handleProjectCreated = (newProject) => {
    dispatch(addProject(newProject));
    setCreateProjectModalOpen(false);
    if (newProject?._id) {
      navigate(urls.projectDetails.replace(':projectId', newProject._id));
    }
  };

  const handleImportComplete = async (importData, isNewProject) => {
    // If a new project was created, refresh the projects list
    if (isNewProject) {
      try {
        // Fetch fresh projects (bypass cache to get updated data)
        const projectsData = await projectService.getUserProjects({ useCache: false });
        if (projectsData?.data?.projects) {
          dispatch(
            setProjects({
              projects: projectsData.data.projects,
              recentProjectIds: projectsData.data.recentProjectIds || [],
            })
          );
        }
      } catch (err) {
        console.error('Error refreshing projects after import:', err);
      }
    }

    // Navigate to the project that was imported into
    if (importData?.project?._id) {
      navigate(urls.projectDetails.replace(':projectId', importData.project._id));
    }
  };

  const onProjectCardClick = (project) => {
    navigate(urls.projectDetails.replace(':projectId', project._id));
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h4">Loading...</Typography>
      </Box>
    );
  }

  // Dark mode background styling with texture and gradients
  const dashboardBackgroundStyle = {
    position: 'relative',
    minHeight: '100%',
    background: isDark
      ? `radial-gradient(ellipse at center, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.8) 100%),
         linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)`
      : `radial-gradient(ellipse at center, rgba(248, 250, 252, 0.6) 0%, rgba(241, 245, 249, 0.9) 100%),
         linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: isDark ? 0.03 : 0.02,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isDark
        ? 'radial-gradient(ellipse 80% 50% at 50% 0%, transparent 0%, rgba(15, 23, 42, 0.4) 100%)'
        : 'radial-gradient(ellipse 80% 50% at 50% 0%, transparent 0%, rgba(248, 250, 252, 0.3) 100%)',
      pointerEvents: 'none',
    },
  };

  return (
    <Box sx={dashboardBackgroundStyle}>
      {/* Vignette overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(ellipse at center, transparent 0%, rgba(15, 23, 42, 0.6) 100%)'
            : 'radial-gradient(ellipse at center, transparent 0%, rgba(248, 250, 252, 0.4) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          padding: { xs: 5, md: 8, lg: 10 },
        }}
      >
        {/* Header Section - Weekly Word Count */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 8,
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: colors.text.primary,
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
            }}
          >
            {getWeeklyWordCountMessage(weeklyWordCount)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => setCreateProjectModalOpen(true)}
              sx={{
                backgroundColor: colors.interactive.primary,
                color: colors.text.primary,
                '&:hover': {
                  backgroundColor: colors.interactive.hover,
                },
              }}
            >
              Create a Project
            </Button>
          </Box>
        </Box>

        {loading || projectsLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <MotionConfig reducedMotion="user">
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Recent Projects Section - hidden while filtering, since the
                  filtered results below cover the full project list */}
              <AnimatePresence initial={false}>
                {!filtersActive && recentProjects.length > 0 && (
                  <motion.div
                    key="recent-projects"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box sx={{ mb: 10 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: '0.15em',
                          mb: 5,
                          fontWeight: 600,
                          color: colors.text.secondary,
                          fontSize: '0.875rem',
                        }}
                      >
                        Recent Projects
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 5,
                          overflowX: 'auto',
                          paddingBottom: 4,
                          '&::-webkit-scrollbar': {
                            height: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: colors.background.secondary,
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: colors.border.subtle,
                            borderRadius: '4px',
                            '&:hover': {
                              background: colors.border.secondary,
                            },
                          },
                        }}
                      >
                        {recentProjects.map((project) => (
                          <Box
                            key={project._id}
                            onClick={() => onProjectCardClick(project)}
                            sx={{
                              cursor: 'pointer',
                              flexShrink: 0,
                            }}
                          >
                            <ProjectCard project={project} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* All Projects Section */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 3,
                    mb: 6,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        fontWeight: 600,
                        color: colors.text.secondary,
                        fontSize: '0.875rem',
                      }}
                    >
                      All Projects
                    </Typography>
                    {filtersActive && (
                      <Typography
                        sx={{ color: colors.text.muted, fontSize: '0.75rem' }}
                        aria-live="polite"
                      >
                        {visibleProjects.length} of {projects.length}
                      </Typography>
                    )}
                  </Box>
                  <DashboardToolbar
                    filters={filters}
                    onFiltersChange={setFilters}
                    typeOptions={typeOptions}
                    statusOptions={statusOptions}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    sortKey={sortKey}
                    onSortChange={setSortKey}
                  />
                </Box>

                {projects.length === 0 && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="30vh"
                    sx={{
                      backgroundColor: colors.background.secondary,
                      borderRadius: '12px',
                      padding: 6,
                      border: `1px solid ${colors.border.subtle}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
                      No projects yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.text.muted }}>
                      Create your first writing project to get started
                    </Typography>
                  </Box>
                )}

                {projects.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        viewMode === 'grid' ? 'repeat(auto-fill, minmax(240px, 1fr))' : '1fr',
                      justifyItems: viewMode === 'grid' ? 'center' : 'stretch',
                      gap: viewMode === 'grid' ? 5 : 2,
                    }}
                  >
                    {/* popLayout pops exiting cards out of the grid flow so the
                        remaining cards slide into place while the removed one
                        fades/scales away — no hard jumps. */}
                    <AnimatePresence mode="popLayout" initial={false}>
                      {visibleProjects.map((project) => (
                        <motion.div
                          key={project._id}
                          layout
                          variants={ITEM_VARIANTS}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          transition={ITEM_TRANSITION}
                          style={viewMode === 'list' ? { width: '100%' } : undefined}
                        >
                          {viewMode === 'grid' ? (
                            <ProjectCard project={project} />
                          ) : (
                            <ProjectListRow project={project} />
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </Box>
                )}

                {/* Filtered-everything-out state */}
                <AnimatePresence>
                  {projects.length > 0 && filtersActive && visibleProjects.length === 0 && (
                    <motion.div
                      key="no-filter-matches"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          backgroundColor: colors.background.secondary,
                          borderRadius: '12px',
                          padding: 6,
                          border: `1px dashed ${colors.border.subtle}`,
                        }}
                      >
                        <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
                          No projects match your filters
                        </Typography>
                        <Button
                          onClick={() => setFilters({ ...DEFAULT_PROJECT_FILTERS })}
                          sx={{
                            color: colors.interactive.primary,
                            textTransform: 'none',
                          }}
                        >
                          Clear filters
                        </Button>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
          </MotionConfig>
        )}

        <CreateProjectModal
          open={isCreateProjectModalOpen}
          onClose={() => setCreateProjectModalOpen(false)}
          onProjectCreated={handleProjectCreated}
          onImportComplete={handleImportComplete}
          projects={projects}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
